/**
 * The component kit.
 *
 * Every component, in every state, on one page, so the quality bar gets set
 * and reviewed once here instead of twenty times across the screen set. The
 * page is a ledger: hairline separated sections, an 11px mono caps label over
 * every state, and no cards around anything that is not itself a card.
 *
 * One doctrine rule is deliberately suspended on this page and nowhere else:
 * "at most one thing moving per screen". A gallery of asynchronous states has
 * to show the moving states. Every product screen keeps the rule.
 */

import { Suspense, lazy, useState } from 'react';

import { Button } from '../components/primitives/Button';
import { Checkbox } from '../components/primitives/Checkbox';
import { Dialog } from '../components/primitives/Dialog';
import { Field } from '../components/primitives/Field';
import { Select } from '../components/primitives/Select';
import { Table } from '../components/primitives/Table';
import { Tabs } from '../components/primitives/Tabs';
import { ToastHost, showToast } from '../components/primitives/Toast';

import { AdvancedDisclosure } from '../components/patterns/AdvancedDisclosure';
import { AsyncNarration, NarratedLine, StepLedger } from '../components/patterns/AsyncNarration';
import { CopyField } from '../components/patterns/CopyField';
import { DataGrid } from '../components/patterns/DataGrid';
import { DestructiveConfirm } from '../components/patterns/DestructiveConfirm';
import { EmptyState } from '../components/patterns/EmptyState';
import { ErrorBlock } from '../components/patterns/ErrorBlock';
import { CorridorStill, SkyStill } from '../components/patterns/LandingStills';
import { StatusPill } from '../components/patterns/StatusPill';
import { TrafficLight } from '../components/patterns/TrafficLight';
import type { TrafficInput } from '../components/patterns/TrafficLight';
import { UsageBar } from '../components/patterns/UsageBar';

import { t } from '../lib/i18n/t';
import { useNearViewport } from '../lib/hooks/use-near-viewport';
import { useDemoStore } from '../lib/mock/store';
import type { DbColumn, DbRow, StatusValue } from '../lib/types';

import '../styles/components/kit.css';

/**
 * The two landing pieces, lazily, on the gallery too.
 *
 * They are marketing decoration and they are not allowed into the console
 * bundle, so the gallery reaches them through the same two chunks the landing
 * route does rather than importing them directly. Nothing is fetched until a
 * reviewer actually scrolls to the bottom of this page, which is why these two
 * sections are last: a reviewer who came here to look at a button never pays
 * for a shader.
 */
const WaveField = lazy(() => import('../components/patterns/WaveField'));
const CloudCorridor = lazy(() => import('../components/patterns/CloudCorridor'));

/** A sized, isolated box, because both pieces fill a parent they do not size. */
function Band({ children }: { children: React.ReactNode }) {
  return <div className="kit__band">{children}</div>;
}

/* ------------------------------------------------------------ page furniture */

function Section({
  name,
  ref_,
  brief,
  children,
}: {
  name: string;
  ref_: string;
  brief: string;
  children: React.ReactNode;
}) {
  const id = `kit-${name}`;
  return (
    <section className="kit__section" aria-labelledby={id}>
      <div className="kit__head">
        <h2 className="kit__name" id={id}>
          {name}
        </h2>
        <span className="kit__ref">{ref_}</span>
      </div>
      <p className="kit__brief">{brief}</p>
      {children}
    </section>
  );
}

function States({ wide, children }: { wide?: boolean; children: React.ReactNode }) {
  return (
    <div className="kit__states" data-wide={wide ? 'yes' : 'no'}>
      {children}
    </div>
  );
}

function State({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="kit__state">
      <span className="kit__state-label">{label}</span>
      <div className="kit__stage">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ fixtures */

const ALL_STATUSES: StatusValue[] = [
  'live',
  'ready',
  'provisioning',
  'deploying',
  'didnt-work',
  'down',
  'paused',
  'replaced',
  'cleaning-up',
];

const HEALTHY: TrafficInput = {
  status: 'live',
  downMinutes: 0,
  lastDeployFailed: false,
  runningSince: '18 August',
  failedAddOn: null,
  overLimitResource: null,
  approaching: null,
  errorsLastHour: 0,
  requestsLastHour: 900,
  hasTraffic: true,
  hasDeploys: true,
};

const FIX_REQUEST = `My app is hosted on SV Cloud. A deploy just failed and I need you to fix it.

Project: Studio Ledger
Deploy: 7c04e1b "Fix the currency formatting typo"
Build step that failed: Build your app

The build error:

  src/routes/invoices.ts(42,10): error TS2551: Property 'formatCurency' does
  not exist on type 'typeof import("/src/lib/money")'. Did you mean
  'formatCurrency'?

    40 |   const rows = await listInvoices(db, clientId);
    41 |   const total = rows.reduce((a, r) => a + r.amount, 0);
  > 42 |   return money.formatCurency(total, "THB");
       |          ^^^^^^^^^^^^^^^^^^
    43 | }

Please find the cause, fix it in my code, and commit the fix to main. Pushing
to main deploys automatically, so do not run any deploy commands.`;

const BUILD_LOG = `19:58:11  Build your app
19:58:11    Compiling 23 files
19:58:24    src/routes/invoices.ts(42,10): error TS2551: Property
19:58:24    'formatCurency' does not exist on type 'typeof
19:58:24    import("/src/lib/money")'. Did you mean 'formatCurrency'?
19:58:24
19:58:24      40 |   const rows = await listInvoices(db, clientId);
19:58:24      41 |   const total = rows.reduce((a, r) => a + r.amount, 0);
19:58:24    > 42 |   return money.formatCurency(total, "THB");
19:58:24         |          ^^^^^^^^^^^^^^^^^^
19:58:24      43 | }
19:58:24
19:58:24    Found 1 error.
19:58:26    Build failed after 15.9s`;

const DB_PROMPT = `My app is hosted on SV Cloud and it already has a database connected. I need you to create the tables it needs. Write the schema and the migration in my code, then commit to main so it deploys. Do not create the database itself, it already exists, and do not ask me for a connection string.`;

interface DeployRowData {
  id: string;
  message: string;
  author: string;
  sha: string;
  seconds: string;
}

const DEPLOY_ROWS: DeployRowData[] = [
  { id: 'd1', message: 'Fix the currency formatting typo', author: 'sam', sha: '7c04e1b', seconds: '15.9s' },
  { id: 'd2', message: 'Add the client filter to invoices', author: 'sam', sha: 'b8f2a06', seconds: '48.2s' },
  { id: 'd3', message: 'Update the footer copy', author: 'nina', sha: '2ad91ff', seconds: '41.7s' },
  { id: 'd4', message: 'First deploy', author: 'sam', sha: '9f2a1c4', seconds: '62.0s' },
];

const GRID_COLUMNS: DbColumn[] = [
  { name: 'id', type: 'uuid', nullable: false, primaryKey: true },
  { name: 'client', type: 'text', nullable: false, primaryKey: false },
  { name: 'amount', type: 'number', nullable: false, primaryKey: false },
  { name: 'paid', type: 'boolean', nullable: false, primaryKey: false },
  { name: 'note', type: 'text', nullable: true, primaryKey: false },
];

const GRID_ROWS: DbRow[] = [
  { id: 'r1', cells: { id: 'inv_9f2a1c', client: 'Baan Studio', amount: 18500, paid: true, note: null } },
  { id: 'r2', cells: { id: 'inv_7c04e1', client: 'Lertlah', amount: 42000, paid: false, note: 'net 30' } },
  { id: 'r3', cells: { id: 'inv_b8f2a0', client: 'Herb and Ash', amount: 7250, paid: true, note: null } },
  { id: 'r4', cells: { id: 'inv_2ad91f', client: 'Soi Cats', amount: 1200, paid: false, note: 'waiting' } },
];

/* ---------------------------------------------------------------- the screen */

export default function Kit() {
  const [tab, setTab] = useState('deploys');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [landingRef, landingNear] = useNearViewport<HTMLDivElement>('200px');

  const speed = useDemoStore((s) => s.prefs.speed);
  const nextBuildFails = useDemoStore((s) => s.prefs.nextBuildFails);
  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const kitTimeline = timelines.find((line) => line.id === 'kit-narration');

  const buildError = (
    <ErrorBlock
      summary={t('Your code has a spelling mistake in a function name.')}
      cause={t(
        'One file calls a function named formatCurency, but the file that defines it spells it formatCurrency. The build stopped before it could finish, so nothing changed on your live site.',
      )}
      location="src/routes/invoices.ts line 42"
      fixRequest={FIX_REQUEST}
      contextAction={{ label: t('Try this deploy again'), onAct: () => undefined }}
      rawLabel={t('Show the build log')}
      raw={BUILD_LOG}
    />
  );

  return (
    <div className="page">
      <header className="page__head">
        <p className="page__eyebrow">M1</p>
        <h1 className="page__title">{t('Component kit')}</h1>
        <p className="page__lede">
          {t(
            'Every component in every state, on one page. The quality bar gets set and reviewed here, once, instead of on twenty screens.',
          )}
        </p>
      </header>

      <div className="kit__note">
        <p className="kit__note-line">
          {t(
            'Press D for the demo dock. It flips the brand direction between Instrument and Cleanroom, the theme between Night and Day, and the language between English, Thai and the pseudo locale that proves the layout survives 1.4x expansion.',
          )}
        </p>
        <p className="kit__note-line kit__note-line--quiet">
          {t(
            'Nothing on this page names a color, a font or a duration. Every flip is a token change, so a direction that looks wrong here is a token file to fix, not twenty components.',
          )}
        </p>
        <p className="kit__note-line kit__note-line--quiet">
          {t(
            'The Advanced state of every disclosure is one URL away: open this page with ?adv=1, or use the Simple and Advanced switch at the bottom of the rail. Both open the same page, so the two artboards can sit side by side.',
          )}
        </p>
      </div>

      <div className="kit">
        {/* ---------------------------------------------------- primitives */}

        <Section
          name="Button"
          ref_="primitive"
          brief={t(
            'Exactly one maximum contrast ink block per screen, and it is always the next thing to do. Everything else is an outline or a bare label. Disabled sits flat, because an inert control must not read as available. Danger is an outline, never a fill.',
          )}
        >
          <States>
            <State label={t('SOLID, THE INK BLOCK')}>
              <Button variant="solid">{t('Visit your app')}</Button>
            </State>
            <State label={t('OUTLINE')}>
              <Button variant="outline">{t('See what happened')}</Button>
            </State>
            <State label={t('GHOST')}>
              <Button variant="ghost">{t('Cancel')}</Button>
            </State>
            <State label={t('DANGER OUTLINE')}>
              <Button variant="danger-outline">{t('Delete Herb and Ash')}</Button>
            </State>
            <State label={t('DISABLED')}>
              <Button variant="solid" disabled>
                {t('Visit your app')}
              </Button>
            </State>
            <State label={t('LOADING')}>
              <Button variant="solid" loading>
                {t('Saving')}
              </Button>
            </State>
            <State label={t('SIZES 28 / 32 / 36')}>
              <div className="kit__inline">
                <Button size="sm">{t('Small')}</Button>
                <Button size="md">{t('Medium')}</Button>
                <Button size="lg">{t('Large')}</Button>
              </div>
            </State>
          </States>
        </Section>

        <Section
          name="Field"
          ref_="primitive"
          brief={t(
            'Label, control, then help or error, never both at once. Mono is reserved for a copyable literal: a key name, an identifier, a project slug.',
          )}
        >
          <States>
            <State label={t('DEFAULT')}>
              <Field label={t('Project name')} defaultValue="Studio Ledger" />
            </State>
            <State label={t('WITH HELP')}>
              <Field
                label={t('Web address')}
                defaultValue="studio-ledger"
                help={t('This is the address people will type to reach your app.')}
              />
            </State>
            <State label={t('ERROR')}>
              <Field
                label={t('Key name')}
                defaultValue="STRIPE KEY"
                mono
                error={t('Key names can use letters, numbers and underscores.')}
              />
            </State>
            <State label={t('DISABLED')}>
              <Field label={t('Region')} defaultValue="Singapore" disabled />
            </State>
          </States>
        </Section>

        <Section
          name="Select"
          ref_="primitive"
          brief={t(
            'A native select, restyled. The platform keyboard map, the mobile picker and the screen reader behavior come for free, and nothing in this product is worth reimplementing them for.',
          )}
        >
          <States>
            <State label={t('DEFAULT')}>
              <Select
                label={t('Branch')}
                options={[
                  { value: 'main', label: 'main' },
                  { value: 'staging', label: 'staging' },
                ]}
              />
            </State>
            <State label={t('WITH HELP')}>
              <Select
                label={t('Plan')}
                options={[
                  { value: 'free', label: t('Plan') },
                  { value: 'starter', label: t('Starter') },
                ]}
                help={t('Changing your plan takes effect on your next bill.')}
              />
            </State>
            <State label={t('DISABLED')}>
              <Select
                label={t('Environment')}
                options={[{ value: 'production', label: t('Production') }]}
                disabled
              />
            </State>
          </States>
        </Section>

        <Section
          name="Checkbox"
          ref_="primitive"
          brief={t(
            'The box is drawn, the input is real. One line of consequence sits under the label, in the vocabulary a person already uses.',
          )}
        >
          <States>
            <State label={t('UNCHECKED')}>
              <Checkbox label={t('Email me when a deploy does not work')} />
            </State>
            <State label={t('CHECKED, WITH HELP')}>
              <Checkbox
                label={t('Show technical details everywhere')}
                help={t('This is the same switch as Advanced in your account menu.')}
                defaultChecked
              />
            </State>
            <State label={t('DISABLED')}>
              <Checkbox label={t('Send weekly reports')} disabled />
            </State>
          </States>
        </Section>

        <Section
          name="Dialog"
          ref_="primitive"
          brief={t(
            'A scrim, an opaque panel, trapped focus, and one escape route that always works. Opening moves focus into the panel, Tab cycles inside it, Escape and the scrim both close, and closing returns focus to whatever opened it.',
          )}
        >
          <States wide>
            <State label={t('LIVE, FOCUS TRAPPED')}>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                {t('Open the dialog')}
              </Button>
              <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={t('Put the last working version back?')}
                description={t(
                  'Your app will go back to the version from 18 August. Nothing in your database changes.',
                )}
                footer={
                  <>
                    <Button variant="outline" onClick={() => setDialogOpen(false)} data-autofocus="">
                      {t('Cancel')}
                    </Button>
                    <Button variant="solid" onClick={() => setDialogOpen(false)}>
                      {t('Put that version back')}
                    </Button>
                  </>
                }
              />
            </State>
            <State label={t('AS A STILL')}>
              <Dialog
                open
                inline
                onClose={() => undefined}
                title={t('Put the last working version back?')}
                description={t(
                  'Your app will go back to the version from 18 August. Nothing in your database changes.',
                )}
                footer={
                  <>
                    <Button variant="outline">{t('Cancel')}</Button>
                    <Button variant="solid">{t('Put that version back')}</Button>
                  </>
                }
              />
            </State>
          </States>
        </Section>

        <Section
          name="Tabs"
          ref_="primitive"
          brief={t(
            'The ledger version: a hairline runs the width of the container and the selected tab thickens its own segment. Arrows move, Home and End jump, and only the selected tab is in the tab order.',
          )}
        >
          <States wide>
            <State label={t('SELECTED, WITH COUNTS')}>
              <Tabs
                label={t('Project sections')}
                value={tab}
                onChange={setTab}
                tabs={[
                  { id: 'deploys', label: t('Deploys'), count: '24' },
                  { id: 'errors', label: t('Errors'), count: '3' },
                  { id: 'activity', label: t('Activity') },
                ]}
              >
                <p className="kit__brief">
                  {t('The panel is a real tab panel, and it is focusable, so a keyboard reaches it.')}
                </p>
              </Tabs>
            </State>
          </States>
        </Section>

        <Section
          name="Table"
          ref_="primitive"
          brief={t(
            'The header sticks, so a scrolled list never loses what its columns mean. The numeric column is mono, tabular and right aligned. A column of words never gets the mono flag, however much it would tidy the alignment.',
          )}
        >
          <States wide>
            <State label={t('STICKY HEADER, MONO NUMERIC COLUMN')}>
              <Table<DeployRowData>
                caption={t('Recent deploys')}
                rows={DEPLOY_ROWS}
                rowKey={(row) => row.id}
                maxHeight="180px"
                columns={[
                  { key: 'message', header: t('What changed'), render: (row) => row.message },
                  { key: 'author', header: t('Who'), render: (row) => row.author },
                  { key: 'sha', header: t('Commit'), mono: true, render: (row) => row.sha },
                  {
                    key: 'seconds',
                    header: t('Took'),
                    align: 'end',
                    mono: true,
                    render: (row) => row.seconds,
                  },
                ]}
              />
            </State>
          </States>
        </Section>

        <Section
          name="Toast"
          ref_="primitive"
          brief={t(
            'One at a time, bottom centre, four seconds, confirmations only. There is no error tone and there will not be one: a message a person has to read must not be able to leave on its own, so errors are owned by the page.',
          )}
        >
          <States>
            <State label={t('CONFIRMATION')}>
              <Button
                variant="outline"
                onClick={() => showToast(t('Your key was saved.'), 'STRIPE_SECRET_KEY')}
              >
                {t('Show a toast')}
              </Button>
            </State>
          </States>
        </Section>

        {/* ------------------------------------------------------ patterns */}

        <Section
          name="StatusPill"
          ref_="4.1"
          brief={t(
            'Shape coded and word labeled, without exception. Five shapes, three colors, and the word is never dropped at any size. Turn every color off and the screen still works. Nothing glows: Instrument carries enabled and inert through fill weight instead.',
          )}
        >
          <States>
            {ALL_STATUSES.map((value) => (
              <State label={value.toUpperCase()} key={value}>
                <div className="kit__stack">
                  <StatusPill value={value} />
                  <StatusPill value={value} size="sm" />
                </div>
              </State>
            ))}
          </States>
        </Section>

        <Section
          name="TrafficLight"
          ref_="4.2"
          brief={t(
            'One per project, ever. A pure precedence function picks the single sentence that answers whether the app is fine, and gives it exactly one action, which is the screen ink block. Thresholds on screen so they can be argued about: 80 percent of a resource, and 5 percent of requests or 10 errors in an hour, over a floor of 20 requests.',
          )}
        >
          <States wide>
            <State label={t('1 DOWN')}>
              <TrafficLight input={{ ...HEALTHY, status: 'down', downMinutes: 12 }} />
            </State>
            <State label={t('2 PAUSED')}>
              <TrafficLight input={{ ...HEALTHY, status: 'paused' }} />
            </State>
            <State label={t('3 DEPLOY DID NOT WORK')}>
              <TrafficLight input={{ ...HEALTHY, lastDeployFailed: true }} />
            </State>
            <State label={t('4 ADD-ON DID NOT FINISH')}>
              <TrafficLight input={{ ...HEALTHY, failedAddOn: t('email add-on') }} />
            </State>
            <State label={t('5 OVER THE LIMIT')}>
              <TrafficLight input={{ ...HEALTHY, overLimitResource: t('database rows') }} />
            </State>
            <State label={t('6 CLOSE TO THE LIMIT')}>
              <TrafficLight
                input={{
                  ...HEALTHY,
                  approaching: { resource: t('requests'), used: '8,400', limit: '10,000' },
                }}
              />
            </State>
            <State label={t('7 ERRORS ELEVATED')}>
              <TrafficLight input={{ ...HEALTHY, errorsLastHour: 14, requestsLastHour: 210 }} />
            </State>
            <State label={t('8 FINE, WITH TRAFFIC')}>
              <TrafficLight input={HEALTHY} />
            </State>
            <State label={t('9 FINE, NO VISITORS')}>
              <TrafficLight input={{ ...HEALTHY, hasTraffic: false, requestsLastHour: 0 }} />
            </State>
            <State label={t('10 READY FOR FIRST DEPLOY')}>
              <TrafficLight input={{ ...HEALTHY, hasDeploys: false, hasTraffic: false, requestsLastHour: 0 }} />
            </State>
          </States>
        </Section>

        <Section
          name="ErrorBlock"
          ref_="4.3"
          brief={t(
            'Summary, plain cause with the location on its own line, actions, raw disclosure. The last two are never optional. The fix request is written as a prompt, it carries the code context, and it closes by telling the agent not to deploy, which is the most valuable line in the payload.',
          )}
        >
          <States wide>
            <State label={t('BUILD FAILURE, THE CANONICAL USE')}>{buildError}</State>
            <State label={t('RUNTIME ERROR GROUP')}>
              <ErrorBlock
                summary={t('Some invoices have no line items, and your app tries to add them up anyway.')}
                cause={t(
                  'One file reads a total on an invoice that came back empty. It happens when an invoice was created but nothing was added to it yet.',
                )}
                location="src/routes/invoices.ts line 88"
                fixRequest={FIX_REQUEST}
                contextAction={{ label: t('Try again'), onAct: () => undefined }}
                rawLabel={t('Show the stack trace')}
                raw={"TypeError: Cannot read properties of null (reading 'total')\n    at invoiceTotal (src/routes/invoices.ts:88:24)\n    at listInvoices (src/routes/invoices.ts:33:11)"}
              />
            </State>
          </States>
        </Section>

        <Section
          name="UsageBar"
          ref_="4.4"
          brief={t(
            'The bar is decoration, the number is the content. Zero is a real state with its own words, not an empty track. Over the limit draws the fill to the end, hatches past the end of the track, and writes the overage in words. A percent-only meter drops the amount entirely: an abstract unit has no reading a person can act on.',
          )}
        >
          <States>
            <State label={t('NO TRAFFIC YET')}>
              <UsageBar label={t('Requests')} used={0} limit={10000} />
            </State>
            <State label={t('UNDER 80 PERCENT')}>
              <UsageBar label={t('Requests')} used={4231} limit={10000} resetsInDays={11} />
            </State>
            <State label={t('AT 80 PERCENT')}>
              <UsageBar label={t('Database rows')} used={8000} limit={10000} />
            </State>
            <State label={t('AT 100 PERCENT')}>
              <UsageBar label={t('Build minutes')} used={100} limit={100} unit="minutes" />
            </State>
            <State label={t('OVER THE LIMIT')}>
              <UsageBar
                label={t('File storage')}
                used={1_395_864_371}
                limit={1_073_741_824}
                unit="bytes"
              />
            </State>
            <State label={t('COMPACT')}>
              <UsageBar label={t('Requests')} used={4231} limit={10000} compact />
            </State>
            {/* The compute variant: an abstract unit has no readable amount,
                so the percentage replaces it rather than sitting beside it. */}
            <State label={t('PERCENT ONLY')}>
              <UsageBar
                label={t('Compute Usage')}
                used={62_891}
                limit={150_000}
                percentOnly
                showPercent
                resetsInDays={11}
              />
            </State>
            {/* The account roll-up: the fill split by the projects that spent
                it, the top three by name and the rest as Others. */}
            <State label={t('BY PROJECT')}>
              <UsageBar
                label={t('Compute Usage')}
                used={9_240_000}
                limit={15_000_000}
                percentOnly
                showPercent
                segments={[
                  { key: 'a', label: 'Herb and Ash', used: 4_610_000 },
                  { key: 'b', label: 'Studio Ledger', used: 2_380_000 },
                  { key: 'c', label: 'Soi Cats', used: 1_420_000 },
                  { key: 'others', label: t('Others'), used: 830_000 },
                ]}
              />
            </State>
            <State label={t('BY PROJECT, NEAR')}>
              <UsageBar
                label={t('Compute Usage')}
                used={13_100_000}
                limit={15_000_000}
                percentOnly
                showPercent
                segments={[
                  { key: 'a', label: 'Herb and Ash', used: 9_800_000 },
                  { key: 'b', label: 'Studio Ledger', used: 3_300_000 },
                ]}
              />
            </State>
          </States>
        </Section>

        <Section
          name="DestructiveConfirm"
          ref_="4.5"
          brief={t(
            'Type to confirm is reserved for the irreversible. Using it on reversible actions trains people to type through dialogs, which is what makes it stop working on the one that matters. The enumeration mixes what survives with what does not, and the action repeats the verb and the object.',
          )}
        >
          <States wide>
            <State label={t('IRREVERSIBLE, INERT UNTIL THE NAME MATCHES')}>
              <DestructiveConfirm
                open
                inline
                onClose={() => undefined}
                onConfirm={() => undefined}
                title={t('Delete Herb and Ash?')}
                objectName="Herb and Ash"
                confirmLabel={t('Delete Herb and Ash')}
                typeToConfirm
                consequences={[
                  { kind: 'loss', text: t('Your web address stops working straight away.') },
                  { kind: 'loss', text: t('Your database and everything in it is deleted.') },
                  { kind: 'loss', text: t('Uploaded files are deleted.') },
                  { kind: 'survives', text: t('Your code on GitHub is untouched.') },
                  { kind: 'survives', text: t('Your other projects keep running.') },
                ]}
              />
            </State>
            <State label={t('REVERSIBLE, NO TYPING')}>
              <DestructiveConfirm
                open
                inline
                onClose={() => undefined}
                onConfirm={() => undefined}
                title={t('Delete this key?')}
                objectName="STRIPE_SECRET_KEY"
                confirmLabel={t('Delete this key')}
                consequences={[
                  { kind: 'loss', text: t('Your app stops being able to use this key on the next deploy.') },
                  { kind: 'survives', text: t('You can add it again at any time.') },
                ]}
              />
            </State>
            <State label={t('LIVE, ESCAPE AND THE SCRIM BOTH CANCEL')}>
              <Button variant="danger-outline" onClick={() => setConfirmOpen(true)}>
                {t('Delete Herb and Ash')}
              </Button>
              <DestructiveConfirm
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => undefined}
                title={t('Delete Herb and Ash?')}
                objectName="Herb and Ash"
                confirmLabel={t('Delete Herb and Ash')}
                typeToConfirm
                consequences={[
                  { kind: 'loss', text: t('Your web address stops working straight away.') },
                  { kind: 'loss', text: t('Your database and everything in it is deleted.') },
                  { kind: 'survives', text: t('Your code on GitHub is untouched.') },
                ]}
                narration={
                  <StepLedger
                    steps={[
                      { label: t('Taking your app offline'), status: 'succeeded', elapsed: '2s' },
                      { label: t('Setting your database aside'), status: 'running', elapsed: '3s' },
                      { label: t('Setting your files aside'), status: 'pending' },
                      { label: t('Releasing your web address'), status: 'pending' },
                    ]}
                  />
                }
              />
            </State>
          </States>
        </Section>

        <Section
          name="EmptyState"
          ref_="4.6"
          brief={t(
            'A restrained line-art mark, one sentence, one action. Never more than one action, and the action is never Learn more. The reassurance state has no button at all, because a button would imply there is something to do.',
          )}
        >
          <States wide>
            <State label={t('NO PROJECTS')}>
              <EmptyState
                mark="box"
                sentence={t("You haven't built anything yet.")}
                action={<Button variant="solid">{t('New project')}</Button>}
              />
            </State>
            <State label={t('NO TABLES, THE AGENT PROMPT VARIANT')}>
              <EmptyState
                mark="table"
                sentence={t('Tables will appear here when your app creates them.')}
                agentPrompt={DB_PROMPT}
              />
            </State>
            <State label={t('NO FILES')}>
              <EmptyState
                mark="file"
                sentence={t("Your app hasn't stored any files yet.")}
                action={<Button variant="solid">{t('Upload')}</Button>}
              />
            </State>
            <State label={t('NO KEYS, THE ADD FORM IS THE ACTION')}>
              {/* Spec 4.6 gives this one "the add form itself" as its action,
                  which is an S14 composition rather than a button. It is here
                  so the key mark gets reviewed with the other six instead of
                  landing unseen on the screen that owns it. */}
              <EmptyState mark="key" sentence={t("You haven't added any keys yet.")} />
            </State>
            <State label={t('NO ERRORS, REASSURANCE, NO ACTION')}>
              <EmptyState mark="clear" sentence={t('No errors in the last 7 days.')} />
            </State>
            <State label={t('NO TRAFFIC')}>
              <EmptyState
                mark="chart"
                sentence={t('No visitors yet.')}
                context={t('Usage counts start the first time somebody opens your app.')}
                action={<Button variant="solid">{t('Visit your app')}</Button>}
              />
            </State>
            <State label={t('NO ACTIVITY')}>
              <EmptyState
                mark="clock"
                sentence={t('Nothing has happened yet. Your first deploy will show up here.')}
                action={<Button variant="outline">{t('Deploys')}</Button>}
              />
            </State>
          </States>
        </Section>

        <Section
          name="CopyField"
          ref_="4.7"
          brief={t(
            'A sunken slab, a mono literal, and a copy control pinned right that never shifts layout when its glyph swaps to a check for 1500ms. Click anywhere in the field to copy. Web addresses truncate from the middle so the subdomain and the ending both stay readable. No toast: the check is the receipt.',
          )}
        >
          <States wide>
            <State label={t('DEFAULT')}>
              <CopyField label={t('Web address')} value="https://studio-ledger.svcloud.app" />
            </State>
            <State label={t('LARGE, THE FIRST BUILD PAYOFF')}>
              <CopyField
                label={t('Web address')}
                value="https://studio-ledger.svcloud.app"
                size="lg"
              />
            </State>
            <State label={t('INLINE, IN A TABLE')}>
              <CopyField label={t('Commit')} value="7c04e1b" size="inline" />
            </State>
            <State label={t('MIDDLE TRUNCATED')}>
              <CopyField
                label={t('File link')}
                value="https://files.studio-ledger.svcloud.app/uploads/2026/august/leek-stew.jpg"
                truncateAt={40}
              />
            </State>
            <State label={t('SENSITIVE, MASKED')}>
              <CopyField label={t('Key')} value="sk_live_51QaBcDeFgHiJkLmNoPqRsTuV" sensitive />
            </State>
          </States>
        </Section>

        <Section
          name="AdvancedDisclosure"
          ref_="4.8"
          brief={t(
            'Quiet but not hidden. Expands in place, never a modal, never a route, never above the simple content. It remembers nothing: Advanced mode is the persistence mechanism, and with Advanced on the trigger is not rendered at all.',
          )}
        >
          <States wide>
            <State label={t('COLLAPSED')}>
              <div className="kit__stack">
                <p className="kit__brief">{t('Your app is running the version from 18 August.')}</p>
                <AdvancedDisclosure>
                  <CopyField label={t('Deploy id')} value="dpl_7c04e1b2f9" size="inline" />
                </AdvancedDisclosure>
              </div>
            </State>
            <State label={t('EXPANDED IN PLACE')}>
              <div className="kit__stack">
                <p className="kit__brief">{t('Your app is running the version from 18 August.')}</p>
                <AdvancedDisclosure defaultOpen>
                  <CopyField label={t('Deploy id')} value="dpl_7c04e1b2f9" size="inline" />
                </AdvancedDisclosure>
              </div>
            </State>
            <State label={t('FIRST EXPAND, WITH THE OFFER')}>
              <AdvancedDisclosure defaultOpen offerAdvanced>
                <CopyField label={t('Region')} value="ap-southeast-1" size="inline" />
              </AdvancedDisclosure>
            </State>
          </States>
        </Section>

        <Section
          name="AsyncNarration"
          ref_="4.9"
          brief={t(
            'Never a bare spinner, ever. All steps are visible from the start, none reorder, none disappear, and completed steps stay on screen when a later one fails. Work in flight is neutral, because colored motion reads as an alarm. Progress is read from the clock, so a refresh mid build keeps the build correct.',
          )}
        >
          <States wide>
            <State label={t('LIVE, FROM THE TIMELINE ENGINE')}>
              <div className="kit__stack">
                <div className="kit__inline">
                  <Button
                    variant="solid"
                    onClick={() =>
                      startTimeline({
                        id: 'kit-narration',
                        kind: 'first-build',
                        scriptId: 'first-build',
                        subjectId: 'kit',
                        startedAt: Date.now(),
                        outcome: nextBuildFails ? 'fail@build' : 'success',
                      })
                    }
                  >
                    {t('Run the first build')}
                  </Button>
                  <span className="kit__state-label">
                    {t('Speed {speed}, from the dock', { speed: String(speed) })}
                  </span>
                </div>
                {kitTimeline ? (
                  <AsyncNarration timeline={kitTimeline} error={buildError} />
                ) : (
                  <p className="kit__brief">
                    {t('Press Run to drive this from the same engine the product screens use.')}
                  </p>
                )}
              </div>
            </State>

            <State label={t('STEP LEDGER, ONE STEP FAILED')}>
              {/* The database step carries the mono detail suffix from spec
                  4.9, which is the one succeeded-step variant the engine
                  driven state above never reaches: no script sets a detail
                  yet, and a state the gallery cannot show is a state the next
                  agent implements from prose instead of from a picture. */}
              <StepLedger
                steps={[
                  { label: t('Reading your code'), status: 'succeeded', elapsed: '4s' },
                  { label: t('Installing what your app needs'), status: 'succeeded', elapsed: '11s' },
                  {
                    label: t('Setting up your database'),
                    status: 'succeeded',
                    elapsed: '7s',
                    detail: '6 tables',
                  },
                  { label: t('Connecting all the wires'), status: 'failed', elapsed: '16s' },
                  { label: t('Publishing your web address'), status: 'skipped' },
                ]}
              />
            </State>

            <State label={t('SINGLE NARRATED LINE')}>
              <NarratedLine sentence={t('Uploading leek-stew.jpg')} />
            </State>

            <State label={t('LONG RUNNING, THE ESCAPE HATCH')}>
              <StepLedger
                longRunning
                onLeave={() => undefined}
                steps={[
                  { label: t('Setting up your database'), status: 'succeeded', elapsed: '7s' },
                  { label: t('Connecting all the wires'), status: 'running', elapsed: '94s' },
                  { label: t('Publishing your web address'), status: 'pending' },
                ]}
              />
            </State>
          </States>
        </Section>

        <Section
          name="DataGrid"
          ref_="4.10"
          brief={t(
            'The M1 cut: sticky header, arrow keys and Home and End to move, Enter to edit, Escape to cancel, and staged edits that do not touch the data until Save. Every cell is a literal out of a database, so every cell is mono and numbers are right aligned. Virtualization, the sticky first column and the diff sheet land in M3.',
          )}
        >
          <States wide>
            <State label={t('EDIT A CELL: ENTER, TYPE, ENTER')}>
              <DataGrid label={t('invoices')} columns={GRID_COLUMNS} rows={GRID_ROWS} />
            </State>
          </States>
        </Section>

        {/* ------------------------------------------- the landing pieces */}

        <div ref={landingRef}>
          <Section
            name="WaveField"
            ref_="landing"
            brief={t(
              'The sky, for the signed out landing only. One WebGL canvas whose five colour stops and ten knobs are read from the token layer at mount, so the flip in the demo dock repaints it in one frame with no rebuild and no lost context. Try it: press D, switch the direction, and watch the graphite go violet. It is never mounted in the console.',
            )}
          >
            <States wide>
              <State label={t('THE SKY, RUNNING')}>
                <Band>
                  {landingNear ? (
                    <Suspense fallback={<SkyStill />}>
                      <WaveField />
                    </Suspense>
                  ) : (
                    <SkyStill />
                  )}
                </Band>
              </State>

              <State label={t('THE STILL: REDUCED MOTION, A PHONE, SAVE DATA, NO WEBGL')}>
                <Band>
                  <SkyStill />
                </Band>
              </State>
            </States>
          </Section>

          <Section
            name="CloudCorridor"
            ref_="landing"
            brief={t(
              'The flight. Two rails of cards ride from behind the screen toward the viewer, once. It waits paused on an authored frame, plays exactly one journey when the reader reaches it, and rests on the frame it started from, because every card is offset by one whole cycle divided by the card count. Scrolling through it dollies the camera. Card art is a separate deliverable, so the cards are token plates today.',
            )}
          >
            <States wide>
              <State label={t('ONE JOURNEY, THEN AT REST')}>
                {landingNear ? (
                  <Suspense fallback={<CorridorStill />}>
                    <CloudCorridor />
                  </Suspense>
                ) : (
                  <CorridorStill />
                )}
              </State>

              <State label={t('LEAN: THE GEOMETRY A PHONE GETS')}>
                {landingNear ? (
                  <Suspense fallback={<CorridorStill />}>
                    <CloudCorridor density="lean" />
                  </Suspense>
                ) : (
                  <CorridorStill />
                )}
              </State>

              <State label={t('THE STILL: REDUCED MOTION, A SMALL PHONE, SAVE DATA')}>
                <CorridorStill />
              </State>
            </States>
          </Section>
        </div>
      </div>

      <ToastHost />
    </div>
  );
}
